<!--
  体测计划管理（2026-10-20）

  只服务「独立体测」这一套（sports_test / sports_test_plan）。
  与「随堂测试（ti_you_*）」「体测活动（activity）」是两个独立模块，
  不要在这里引用它们的接口，也不要把它们的数据混进列表。

  一条计划 = 一个学校的一批体测。school 与 sports_test.school 一对一。
-->
<template>
  <div class="plan-page">

    <!-- 工具条 -->
    <div class="btn-toolbar">
      <el-input v-model="keyword" placeholder="计划名 / 学校名" clearable style="width: 240px"
                @keyup.enter="loadList" @clear="loadList"/>
      <el-select v-model="status" placeholder="全部状态" clearable style="width: 150px" @change="loadList">
        <el-option v-for="s in statusOptions" :key="s.value" :label="s.label" :value="s.value"/>
      </el-select>
      <el-button type="primary" icon="Search" @click="loadList">查询</el-button>
      <el-button icon="Refresh" @click="resetQuery">重置</el-button>
      <span class="toolbar-tip">
        只有「可见」的计划才会出现在监考 / 签到 / 成绩查询 / 大屏的学校下拉里；
        已归档 = 强制隐藏（优先于可见截止时间）。
      </span>
    </div>

    <!-- 列表 -->
    <el-table :data="list" v-loading="loading" border size="small" max-height="620">
      <el-table-column prop="planName" label="计划名称" min-width="200" show-overflow-tooltip/>
      <el-table-column prop="school" label="计划标识（学校）" min-width="200" show-overflow-tooltip/>
      <el-table-column prop="testDate" label="测试日期" width="120" align="center">
        <template #default="scope">{{ scope.row.testDate || '—' }}</template>
      </el-table-column>

      <el-table-column label="状态" width="110" align="center">
        <template #default="scope">
          <el-tag :type="statusTagType(scope.row.status)" size="small">
            {{ statusText(scope.row.status) }}
          </el-tag>
        </template>
      </el-table-column>

      <el-table-column label="学员数" width="90" align="center">
        <template #default="scope">
          {{ scope.row.realCount != null ? scope.row.realCount : scope.row.studentCount }}
        </template>
      </el-table-column>

      <el-table-column label="可见截止" width="170" align="center">
        <template #default="scope">
          <span :class="isVisible(scope.row) ? '' : 'text-expired'">
            {{ scope.row.visibleEndTime || '长期可见' }}
          </span>
        </template>
      </el-table-column>

      <el-table-column label="当前" width="80" align="center">
        <template #default="scope">
          <el-tag v-if="isVisible(scope.row)" type="success" size="small">可见</el-tag>
          <el-tag v-else type="info" size="small">隐藏</el-tag>
        </template>
      </el-table-column>

      <el-table-column prop="ownerUserName" label="负责人" width="100">
        <template #default="scope">{{ scope.row.ownerUserName || '—' }}</template>
      </el-table-column>

      <el-table-column prop="importUserName" label="导入人" width="100">
        <template #default="scope">{{ scope.row.importUserName || '—' }}</template>
      </el-table-column>

      <el-table-column label="操作" width="345" fixed="right" align="center">
        <template #default="scope">
          <el-button size="small" type="primary" plain @click="openEdit(scope.row)">编辑</el-button>
          <el-button size="small" @click="gotoStudents(scope.row)">学员</el-button>
          <el-button size="small" type="danger" plain @click="doClear(scope.row)">清空学员</el-button>
          <el-tooltip :disabled="isEmptyPlan(scope.row)" placement="top"
                      content="只有学员数为 0 的计划才能删除；有学员请先「清空学员」">
            <span>
              <el-button size="small" type="danger" text
                         :disabled="!isEmptyPlan(scope.row)"
                         @click="doDelete(scope.row)">删除</el-button>
            </span>
          </el-tooltip>
          <el-dropdown trigger="click" style="margin-left: 8px"
                       @command="cmd => doStatus(scope.row, cmd)">
            <el-button size="small">状态 ▾</el-button>
            <template #dropdown>
              <el-dropdown-menu>
                <el-dropdown-item :command="0" :disabled="scope.row.status === 0">设为待测</el-dropdown-item>
                <el-dropdown-item :command="1" :disabled="scope.row.status === 1">设为进行中</el-dropdown-item>
                <el-dropdown-item :command="2" :disabled="scope.row.status === 2">设为已完成</el-dropdown-item>
                <el-dropdown-item :command="3" :disabled="scope.row.status === 3" divided>归档（隐藏）</el-dropdown-item>
              </el-dropdown-menu>
            </template>
          </el-dropdown>
        </template>
      </el-table-column>
    </el-table>

    <div class="plan-foot">共 {{ list.length }} 条（最多展示 300 条）</div>

    <!-- 编辑计划 -->
    <el-dialog v-model="editDialog" title="编辑体测计划" width="620px">
      <el-form :model="form" label-width="120px" size="small">
        <el-form-item label="计划标识">
          <el-input :model-value="form.school" disabled/>
          <div class="form-hint">
            计划标识 = <code>sports_test.school</code>，与学员表一对一，<b>不允许修改</b>。
            要换学校请新建计划重新导入。
          </div>
        </el-form-item>

        <el-form-item label="计划名称">
          <el-input v-model="form.planName" placeholder="默认与计划标识相同"/>
        </el-form-item>

        <el-form-item label="测试日期">
          <el-date-picker v-model="form.testDate" type="date" value-format="YYYY-MM-DD"
                          placeholder="业务口径的测试日" style="width: 220px"/>
        </el-form-item>

        <el-form-item label="状态">
          <el-select v-model="form.status" style="width: 220px">
            <el-option v-for="s in statusOptions" :key="s.value" :label="s.label" :value="s.value"/>
          </el-select>
        </el-form-item>

        <el-form-item label="可见起止">
          <el-date-picker v-model="form.visibleStartTime" type="datetime"
                          value-format="YYYY-MM-DD HH:mm:ss" placeholder="起始（留空=不限）"
                          style="width: 200px"/>
          <span class="range-sep">至</span>
          <el-date-picker v-model="form.visibleEndTime" type="datetime"
                          value-format="YYYY-MM-DD HH:mm:ss" placeholder="截止（留空=长期可见）"
                          style="width: 200px"/>
        </el-form-item>

        <el-form-item label="评分标准城市">
          <el-input v-model="form.regionName" placeholder="如：郑州" style="width: 160px"/>
          <el-input v-model.number="form.regionId" placeholder="城市ID" style="width: 110px; margin-left: 8px"/>
          <div class="form-hint">仅作登记与提示，实际评分标准仍由导入 Excel 里的城市决定。</div>
        </el-form-item>

        <el-form-item label="测试项目">
          <el-input v-model="form.projects" type="textarea" :rows="2"
                    placeholder="分号分隔，如：身高;体重;肺活量;50米跑"/>
          <div class="form-hint">仅作计划登记；实际建哪些项目仍以导入 Excel 的项目列为准。</div>
        </el-form-item>

        <el-form-item label="负责人">
          <el-input v-model="form.ownerUserName" placeholder="计划负责人姓名" style="width: 200px"/>
        </el-form-item>

        <el-form-item label="备注">
          <el-input v-model="form.remark" type="textarea" :rows="2" placeholder="如：延期原因"/>
        </el-form-item>
      </el-form>

      <template #footer>
        <el-button @click="editDialog = false">取消</el-button>
        <el-button type="primary" :loading="saving" @click="saveEdit">保存</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import {
  getPlanList, updatePlan, updatePlanStatus, clearPlanStudents, deletePlan
} from "@/api/transcript/transcript";

export default {
  name: "PlanList",
  data() {
    return {
      keyword: '',
      status: null,
      list: [],
      loading: false,

      statusOptions: [
        {value: 0, label: '待测'},
        {value: 1, label: '进行中'},
        {value: 2, label: '已完成'},
        {value: 3, label: '已归档'},
      ],

      editDialog: false,
      saving: false,
      /** 状态流转的防重锁：确认框关掉到接口返回之间，用户还能再点一次下拉 */
      statusSaving: false,
      form: {},
    }
  },
  mounted() {
    this.loadList();
  },
  methods: {
    loadList() {
      this.loading = true;
      getPlanList(this.keyword || '', this.status).then(res => {
        if (!res || res.code !== 200) {
          this.list = [];
          this.$message.error((res && res.message) || '加载失败');
          return;
        }
        this.list = res.data || [];
      }).catch(() => {
        this.list = [];
        this.$message.error('加载失败，请重试');
      }).finally(() => {
        this.loading = false;
      });
    },

    resetQuery() {
      this.keyword = '';
      this.status = null;
      this.loadList();
    },

    statusText(status) {
      const map = {0: '待测', 1: '进行中', 2: '已完成', 3: '已归档'};
      return map[status] != null ? map[status] : '待测';
    },

    statusTagType(status) {
      // 注意别写 'primary'：el-tag 的类型里没有它（2.4.x），会渲染成无样式；
      // 进行中就用默认类型（空串），所以这里不能用 || 兜底
      const map = {0: 'info', 1: '', 2: 'success', 3: 'warning'};
      return map[status] != null ? map[status] : 'info';
    },

    /** 后端已按 NOW() 算好 visible；拿不到时前端兜底 */
    isVisible(row) {
      if (!row) return false;
      if (row.visible != null) return !!row.visible;
      if (row.status === 3) return false;
      if (!row.visibleEndTime) return true;
      return new Date(String(row.visibleEndTime).replace(/-/g, '/')).getTime() >= Date.now();
    },

    openEdit(row) {
      this.form = Object.assign({}, row, {
        testDate: row.testDate || '',
        visibleStartTime: row.visibleStartTime || '',
        visibleEndTime: row.visibleEndTime || '',
        regionId: row.regionId || '',
        regionName: row.regionName || '',
        projects: row.projects || '',
        ownerUserName: row.ownerUserName || '',
        remark: row.remark || '',
        status: row.status == null ? 0 : row.status,
      });
      this.editDialog = true;
    },

    saveEdit() {
      if (!this.form.id) {
        this.$message.error('缺少计划 id');
        return;
      }
      // 截止时间填了但已经在过去，会让计划立刻隐身，先拦一下
      if (this.form.visibleEndTime &&
          new Date(this.form.visibleEndTime.replace(/-/g, '/')).getTime() <= Date.now()) {
        this.$message.warning('可见截止时间已经过去，保存后该计划会立刻隐藏；如需长期可见请留空');
        return;
      }
      this.saving = true;
      const payload = {
        id: this.form.id,
        planName: this.form.planName,
        testDate: this.form.testDate || null,
        regionId: this.form.regionId === '' ? null : this.form.regionId,
        regionName: this.form.regionName,
        projects: this.form.projects,
        ownerUserName: this.form.ownerUserName,
        status: this.form.status,
        visibleStartTime: this.form.visibleStartTime || null,
        visibleEndTime: this.form.visibleEndTime || null,
        remark: this.form.remark,
      };
      updatePlan(payload).then(res => {
        if (!res || res.code !== 200) {
          this.$message.error((res && res.message) || '保存失败');
          return;
        }
        this.$message.success('已保存');
        this.editDialog = false;
        this.loadList();
      }).catch(() => {
        this.$message.error('保存失败，请重试');
      }).finally(() => {
        this.saving = false;
      });
    },

    doStatus(row, status) {
      if (row.status === status) return;
      if (this.statusSaving) return;
      // ⚠️ 归档（status=3）是「会立刻让家长查不到成绩」的操作，确认框必须带上人数：
      //    只说「会从下拉里消失」，操作的人根本不知道影响的是 8 个人还是 800 个人。
      const cnt = row.realCount != null ? row.realCount : (row.studentCount || 0);
      const tip = status === 3
          ? `该计划下现有 <b>${cnt}</b> 名学员。<br/><br/>`
            + '归档后会<b>立刻</b>从监考 / 签到 / 成绩查询的下拉里消失（优先于可见截止时间），'
            + '这批学员的家长端也查不到成绩。<br/><br/>'
            + '<span style="color:#e6a23c">如果只是「测试已经做完了、不想再出现在选校列表里」，'
            + '请改「可见截止时间」而不是归档 —— 改时间不影响家长回查成绩。</span>'
          : `确认把「${row.planName || row.school}」的状态改为「${this.statusText(status)}」？`;
      this.$confirm(tip, status === 3 ? '归档计划' : '修改计划状态', {
        type: status === 3 ? 'warning' : 'info',
        confirmButtonText: status === 3 ? '确认归档' : '确认',
        cancelButtonText: '取消',
        dangerouslyUseHTMLString: status === 3
      }).then(() => {
        this.statusSaving = true;
        return updatePlanStatus(row.id, status);
      }).then(res => {
        if (!res || res.code !== 200) {
          this.$message.error((res && res.message) || '修改失败');
          return;
        }
        this.$message.success('状态已更新');
        this.loadList();
      }).catch(err => {
        if (err !== 'cancel' && err !== 'close') this.$message.error('修改失败，请重试');
      }).finally(() => {
        this.statusSaving = false;
      });
    },

    /**
     * 清空计划学员。
     *
     * 🔴 这是物理删除（不是软删除）：sports_test 上有 UNIQUE(student_identity)，
     *    软删除后唯一索引仍被占着，同一个学号再也导不进来。
     *    删之前后端会把整行 JSON 写进 sports_test_delete_archive。
     */
    doClear(row) {
      const count = row.realCount != null ? row.realCount : (row.studentCount || 0);
      if (!count) {
        this.$message.info('该计划下没有学员');
        return;
      }
      this.$prompt(
          `将【物理删除】「${row.planName || row.school}」下的 ${count} 名学员及其成绩。<br/>` +
          '删除会写入归档表（可追溯），但页面不可恢复。<br/>' +
          '清空后可以直接重新导入同一批学号。<br/><br/>请输入「清空」二字确认：',
          '清空计划学员', {
            type: 'warning',
            confirmButtonText: '确认清空',
            cancelButtonText: '取消',
            inputPlaceholder: '请输入：清空',
            dangerouslyUseHTMLString: true,
          }
      ).then(({value}) => {
        if ((value || '').trim() !== '清空') {
          this.$message.warning('输入不匹配，已取消');
          return null;
        }
        return clearPlanStudents(row.id, '计划管理页清空学员');
      }).then(res => {
        if (res === null) return;
        if (!res || res.code !== 200) {
          this.$message.error((res && res.message) || '清空失败');
          return;
        }
        this.$message.success(`已清空 ${res.data != null ? res.data : ''} 名学员，可以重新导入`);
        this.loadList();
      }).catch(err => {
        if (err !== 'cancel' && err !== 'close') this.$message.error('清空失败，请重试');
      });
    },

    /** 该计划是否为空（学员数为 0 才允许删除） */
    isEmptyPlan(row) {
      const n = row && row.realCount != null ? row.realCount : (row ? row.studentCount : 0);
      return !n;
    },

    /**
     * 删除空计划。
     *
     * ⚠️ 为什么需要它：school 上有 UNIQUE。导入时学校名打错一个字母就会留下一个
     *    永远匹配不上学员的空计划，而它还占着这个标识 —— 只能归档藏起来，
     *    列表里永远多一条垃圾。删掉才能真正释放。
     */
    doDelete(row) {
      if (!this.isEmptyPlan(row)) {
        this.$message.warning('该计划下还有学员，请先「清空学员」再删除');
        return;
      }
      this.$confirm(
          `确认删除计划「${row.planName || row.school}」？<br/>` +
          '这是<b>物理删除</b>（为了释放被占用的学校标识），删除后不可恢复。<br/>' +
          '仅学员数为 0 的计划可删除。',
          '删除计划', {
            type: 'warning',
            confirmButtonText: '确认删除',
            cancelButtonText: '取消',
            dangerouslyUseHTMLString: true,
          }
      ).then(() => {
        return deletePlan(row.id);
      }).then(res => {
        if (!res || res.code !== 200) {
          this.$message.error((res && res.message) || '删除失败');
          return;
        }
        this.$message.success('已删除');
        this.loadList();
      }).catch(err => {
        if (err !== 'cancel' && err !== 'close') this.$message.error('删除失败，请重试');
      });
    },

    /** 跳到学员管理并按该计划过滤（学员页用 school 精确过滤） */
    gotoStudents(row) {
      this.$router.push({path: '/transcript', query: {school: row.school}});
    },
  }
}
</script>

<style scoped>
.plan-page {
  padding: 16px 20px;
}

.btn-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  padding: 6px 0 16px;
}

.toolbar-tip {
  flex: 1;
  min-width: 240px;
  font-size: 12px;
  line-height: 1.6;
  color: #909399;
}

.plan-foot {
  padding-top: 10px;
  text-align: right;
  font-size: 12px;
  color: #909399;
}

.text-expired {
  color: #f56c6c;
}

.form-hint {
  margin-top: 4px;
  font-size: 12px;
  line-height: 1.6;
  color: #909399;
}

.range-sep {
  margin: 0 8px;
  color: #909399;
}
</style>
